import { z } from 'zod';

/**
 * Identificadores e primitivos compartilhados.
 * Usar CUID2 (collision-resistant, URL-safe, ordenável) como ID padrão do banco.
 */

export const idSchema = z.string().min(8).max(36).describe('Identificador CUID2 ou UUID');

export const slugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: 'Slug deve ser kebab-case minúsculo, sem acentos ou caracteres especiais.',
  });

export const emailSchema = z.string().email({ message: 'E-mail em formato inválido.' });

export const isoDateTimeSchema = z.string().datetime({ offset: true });

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(20),
});

export type Pagination = z.infer<typeof paginationSchema>;

/**
 * Schema para ordenação. Aceita `sort=campo:dir` (ex.: `?sort=criadoEm:desc`).
 * Default é decidido pelo endpoint, não pelo schema.
 */
export const sortDirectionSchema = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof sortDirectionSchema>;

/**
 * Helper para parsear `?sort=campo:dir` em `{ campo, dir }`.
 * Retorna null se o input for vazio ou malformado.
 */
export function parseSort(input: string | undefined): { campo: string; dir: SortDirection } | null {
  if (!input) return null;
  const [campo, dir = 'asc'] = input.split(':');
  if (!campo) return null;
  const parsedDir = sortDirectionSchema.safeParse(dir);
  if (!parsedDir.success) return null;
  return { campo, dir: parsedDir.data };
}

/**
 * Resposta paginada genérica. Use:
 *   const ArticleListResponse = paginatedResponseSchema(articleSchema);
 */
export function paginatedResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(0),
  });
}

/**
 * Calcula a "página" a partir de total + paginação. Útil em handlers.
 */
export function buildPagination<T>(
  data: T[],
  total: number,
  pagination: Pagination,
): { data: T[]; page: number; perPage: number; total: number; totalPages: number } {
  return {
    data,
    page: pagination.page,
    perPage: pagination.perPage,
    total,
    totalPages: Math.max(1, Math.ceil(total / pagination.perPage)),
  };
}

/**
 * Helper para slugificar strings em pt-BR (remove acentos, normaliza).
 * Replica regra que aceitamos no slugSchema.
 */
export function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove diacríticos
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
